// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { SiteRef } from './types';
import { siteColor } from './format';

export const SiteFav: React.FC<{ site: Pick<SiteRef, 'siteId' | 'name' | 'kind'>; size?: number }> = ({ site, size = 26 }) => (
  <span className="rounded-[7px] flex items-center justify-center text-white font-bold text-[11px] shrink-0" style={{ width: size, height: size, background: siteColor(site.siteId) }}>
    {site.kind === 'platform' ? '◆' : (site.name || '?').charAt(0).toUpperCase()}
  </span>
);

const GROUPS: Array<[string, SiteRef['kind']]> = [['Platform', 'platform'], ['Company', 'company'], ['Tenant websites', 'store']];

/** Searchable website picker (admin only). Custom control — never a native select. */
export const SiteSwitcher: React.FC<{ sites: SiteRef[]; value: string; onChange: (id: string) => void; error?: string | null }> = ({ sites, value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const cur = sites.find((s) => s.siteId === value) || { siteId: value, name: value === '_all' ? 'All websites' : value, kind: value === '_all' ? 'platform' : 'store', hostnames: [] } as SiteRef;
  const storeCount = sites.filter((s) => s.kind !== 'platform').length;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    input.current?.focus();
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (s: SiteRef) => !needle || `${s.name} ${s.siteId} ${s.hostnames.join(' ')}`.toLowerCase().includes(needle);
    return GROUPS.map(([label, kind]) => [label, sites.filter((s) => s.kind === kind && match(s))] as const).filter(([, l]) => l.length);
  }, [sites, q]);

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button" data-testid="wa-site-switcher" aria-haspopup="listbox" aria-expanded={open}
        onClick={() => { setOpen((o) => !o); setQ(''); }}
        className="h-11 min-w-0 sm:min-w-[280px] max-w-full px-3 rounded-[10px] border border-[#333] bg-[#111] text-white flex items-center gap-2.5 text-left"
      >
        <SiteFav site={cur} />
        <span className="min-w-0">
          <b className="block text-[13px] truncate">{cur.name}</b>
          <small className="block text-gray-400 text-[11px] truncate">
            {error && !sites.length ? 'Website list unavailable' : cur.kind === 'platform' ? `Platform view · ${storeCount} sites` : cur.hostnames[0] || cur.siteId}
          </small>
        </span>
        <ChevronDown size={16} className="ml-auto text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute top-[50px] right-0 w-[360px] max-w-[calc(100vw-32px)] bg-white rounded-xl z-50 overflow-hidden text-stone-900" style={{ boxShadow: '0 20px 50px rgba(0,0,0,.35)' }} role="listbox">
          <input
            ref={input} value={q} onChange={(e) => setQ(e.target.value)} data-testid="wa-site-search"
            placeholder="Search websites, domains or tenants…"
            className="w-full border-0 border-b border-stone-200 p-3.5 text-[13px] outline-none text-stone-900 bg-white"
          />
          <div className="max-h-[360px] overflow-auto">
            {groups.length === 0 && <div className="text-[10px] uppercase tracking-wider text-stone-500 px-3.5 pt-2.5 pb-2">{error && !sites.length ? `Couldn't load websites — ${error}` : 'No websites match'}</div>}
            {groups.map(([label, list]) => (
              <div key={label}>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 px-3.5 pt-2.5 pb-1">{label}</div>
                {list.map((s) => (
                  <button
                    key={s.siteId} type="button" role="option" aria-selected={s.siteId === value}
                    onClick={() => { onChange(s.siteId); setOpen(false); }}
                    className={`w-full flex gap-2.5 items-center px-3.5 py-2 min-h-[44px] text-left border-0 hover:bg-[#FFF1F3] ${s.siteId === value ? 'bg-[#FFF1F3]' : 'bg-white'}`}
                  >
                    <SiteFav site={s} />
                    <span className="min-w-0">
                      <b className="block text-[13px] truncate">{s.name}</b>
                      <small className="block text-stone-500 text-[11px] truncate">
                        {s.kind === 'platform' ? 'Every site RubyTill hosts' : s.hostnames.join(' · ') || s.siteId}
                      </small>
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
